// Base model for supporting validation.
// Implements interface to third-party validation tools, and provides standard "validate" method.
// Extend this for other models.
// This module provides:
//    constructor - saves data as properties for keys matching the schema keys
//    validate(callback) - validate the property data against the schema & return results
//    static validate(data, callback) - Combines constructing an object & running validation on the data
//    toObject()  - Returns the object properties as a simple Object

const {validate, validateAll, validations, rule} = require('indicative')
const extend = require('extend')
const moment = require('moment')

// Add our custom/extra Validations
validations.length = (data, field, message, args, get) => {
   return new Promise((resolve, reject) => {
      const value = get(data, field)
      // Blank value passed (unless separate "required" rule active)
      if (!value) return resolve('passed')
      // Assume args is always an array of 0 or 1
      args = args.shift()
      // const equal = args.filter(a => typeof a == 'number')
      if (typeof args == 'number') {
         if (value.length != args)
            return reject('length of '+field+' must equal '+args)
      } else {
         const max = 'maximum' in args ? args.maximum : 'max' in args ? args.max : null
         const min = 'minimum' in args ? args.minimum : 'min' in args ? args.min : null
         // console.log({min, max, value}, value.length)
         if (max !== null && value.length > max)
            return reject('length of '+field+' must not exceed '+max)
         if (min !== null && value.length < min)
            return reject('length of '+field+' must be greater than '+min)
      }
      resolve('passed')
   })
}

// Alternate Date validator using Moment, and format dd/mm/yyyy.
validations.date = (data, field, message, args, get) => {
   return new Promise((resolve, reject) => {
      const value = get(data, field)
      // Blank value passed (unless separate "required" rule active)
      if (!value) return resolve('passed')

      if (!moment(value, 'D/M/YYYY', true).isValid())
         return reject('Please enter the date using the format: dd/mm/yyyy')
      resolve('passed')
   })
}

// Alternate Date "after" validator using Moment.
validations.after = (data, field, message, args, get) => {
   return new Promise((resolve, reject) => {
      const value = get(data, field)
      // Blank value passed (unless separate "required" rule active)
      if (!value) return resolve('passed')

      let the_date = moment(value, 'D/M/YYYY', true)

      if (!the_date.isValid())
         return reject('Please enter the date using the format: dd/mm/yyyy')
      if (the_date.isBefore(args[0]))
         return reject(message || `date must be after ${args[0].format('D/M/YYYY')}`)
      resolve('passed')
   })
}

// No Validation on Hidden Fields (by default)
validations.hidden = (data, field, message, args, get) => {
   resolve('passed')
}

// Optional Phone validations using helper
try {
   const phoneHelper = require('common/helpers/phone-number')
   const phoneMatch = /^\s*\+?[\(\)\d\s]+\s*$/

   validations.mobile = (data, field, message, args, get) => {
      return new Promise((resolve, reject) => {
         const value = get(data, field)
         if (value && !(value.match(phoneMatch) && phoneHelper.isMobile(value)))
            reject(message || field+' is not a valid mobile phone number') //  'is not a valid mobile phone number'
         resolve('passed')
      })
   }
   validations.phone = (data, field, message, args, get) => {
      return new Promise((resolve, reject) => {
         const value = get(data, field)
         if (value && !(value.match(phoneMatch) && phoneHelper.isPossibleNumber(value)))
            reject(message || field+' is not a valid phone number') //  'is not a valid phone number'
         resolve('passed')
      })
   }
} catch (e) {
   // Fallback simplified phone validators
   const phoneMatch = /^\+?[\(\)\d\s]+$/

   validations.mobile = (data, field, message, args, get) => {
      return new Promise((resolve, reject) => {
         const value = get(data, field).trim()
         if (value && !(value.match(phoneMatch) && value.length>=10))
            reject(message || field+' is not a valid mobile phone number') //  'is not a valid mobile phone number'
         resolve('passed')
      })
   }
   validations.phone = (data, field, message, args, get) => {
      return new Promise((resolve, reject) => {
         const value = get(data, field)
         if (value && !(value.match(phoneMatch) && value.length>=8))
            reject(message || field+' is not a valid phone number') //  'is not a valid phone number'
         resolve('passed')
      })
   }
}

// Map of our "type" to Indicative Validations
const types = {
   alphanumeric: 'alpha_numeric',
   checkbox: 'accepted',
   // Alphanumerics, and a few allowed symbols. This could be smarter...
   name: rule('regex', /[a-z\s\-']+/i)
}

class ValidateBase {
   constructor(data) {
      if (!data) return;
      this.data = data
   }

   get schema() {
      return this._schema || { }
   }

   set schema(value) {
      this._schema = value
   }

   set data(data) {
      // Use our schema keys to map data properties to object
      Object.keys(this.schema).forEach(p => this[p] = data[p])
      // Ensure there's no "last validate" data
      delete this._last_validate
   }

   get _indicative_rules() {
      // Convert our schema into required format for validate.js
      let schema = this.schema
      let indrules = {}
      Object.keys(schema).forEach(field => {
         const field_schema = schema[field] || {}
         let indrule = types[field_schema.type]
         if (!indrule && field_schema.type && field_schema.type != 'select') // Select should have an "in" that defines it already
            indrule = [rule(field_schema.type)]
         else
            indrule = []

         if (!Array.isArray(indrule))
            indrule = [indrule]

         if (field_schema.required)
            indrule.push(rule('required'));

         if (field_schema.format)
            indrule.push(rule('regex', field_schema.format))

         ;['equals', 'in', 'length', 'same', 'dateFormat', 'required_when', 'after'].forEach(k => {
            if (field_schema[k])
               indrule.push(rule(k, field_schema[k]))
         })

         // Map certain validation messages
         // if (field_schema.message && 'format' in indrule)
         //    indrule.format.message = field_schema.message

         indrules[field] = indrule
      })
      // console.log('Schema => Indicative', {schema, indrules})
      return indrules
   }

   get _indicative_messages() {
      let indmsgs = {}
      if (!this.messages)
         return
      Object.keys(this.messages).forEach(key => {
         let [all, field, dotrule, rule] = key.match(/^([^\.]+)(|\.([^\.]+))$/)
         let msg = this.messages[key]
         if (all && rule) {
            if (rule == 'format')
               rule = 'regex'
            indmsgs[field+'.'+rule] = msg
         } else {
            indmsgs[key] = msg
         }
      })
      // console.log('Validation Messages', this.messages, indmsgs)
      return indmsgs
   }

   async validate(callback) {
      let errlist = null
      try {
         await validateAll(this, this._indicative_rules, this._indicative_messages)
         this._last_validate = null // Property exists but is null
      } catch (errors) {
         if (errors instanceof Error) throw errors
         // Convert from array of errors to keyed by field name
         this._last_validate = {}
         errors.forEach(err => {
            if (err.field in this._last_validate)
               this._last_validate[err.field].push(err.message)
            else
               this._last_validate[err.field] = [err.message]
         })
      }
      // console.log('Validated', this, this._indicative_rules, this._last_validate)
      return callback ? callback(null, this._last_validate) : this._last_validate
   }

   get isValid() {
      if (!('_last_validate' in this))
         throw(new Error('Calling await o.validate() first is now required'))

      return this._last_validate ? false : true
   }

   get isInvalid() {
      return !this.isValid
   }

   validateSummary() {
      if (this.isValid) // This also inits validation if necessary
         return 'Ok'

      return Object.keys(this._last_validate).map(key => this._last_validate[key].join(', ')).join(', ')
      // return this._last_validate.map(v => v.message).join(', ')
   }

   // Static validate, to allow simple validation of form data without keeping the object around
   static async validate(data, callback) {
      return await (new this(data)).validate(callback)
   }

   // Convert properties to normal data object
   toObject() {
      return extend({}, this)
   }

   // Returns an array of field names - basically the keys of the schema data
   get fields() {
      return Object.keys(this.schema)
   }
}

module.exports = ValidateBase
